import { normaliseArray } from './core-utilities';
import { useSelectCardDetails } from 'selectors/selectors';

export const switchExpiryDate = (date) => {
  // Contains a '/', remove and return reversed values
  if (date?.includes('/')) {
    date = date.replace('/', '');
    return `${date.substr(2, 2)}${date.substr(0, 2)}`;
  }

  // if date is not 4 values, abort
  if (date?.length !== 4) {
    return date;
  }

  return `${date.substr(2, 2)}/${date.substr(0, 2)}`;
};

/**
 * Represents a list of card statuses with associated properties.
 * @typedef {Object} status
 * @property {string} label - The name of the card status.
 * @property {boolean} isFilterOption - Indicates if the status is included in the card filter dropdown.
 * @property {string[]} allowedUpdateTargets - The statuses to which the card can be set.
 * @property {boolean} willReissue - Specifies whether updating to this status will cause a reissue.
 * @property {boolean} otherActionsAllowed - Specifies whether other actions are allowed (Set PIN, Reset PIN, Transfer Card, Update Usage, Edit Card, Activate Card).
 *
 * @type {status[]}
 */
export const cardStatuses = [
  {
    label: 'Processing',
    isFilterOption: true,
    allowedUpdateTargets: ['Requested in Error'],
    willReissue: false,
    otherActionsAllowed: false,
  },
  {
    label: 'Active',
    isFilterOption: true,
    allowedUpdateTargets: ['Damaged', 'Lost', 'Stolen', 'Closed', 'Temporarily Closed'],
    willReissue: false,
    otherActionsAllowed: true,
  },
  {
    label: 'Inactive',
    isFilterOption: true,
    allowedUpdateTargets: [],
    willReissue: false,
    otherActionsAllowed: true,
  },
  {
    label: 'Damaged', // A card should never be shown as Damaged in the cards list
    isFilterOption: false,
    allowedUpdateTargets: [],
    willReissue: true,
    otherActionsAllowed: true,
  },
  {
    label: 'Lost',
    isFilterOption: true,
    allowedUpdateTargets: [],
    willReissue: true,
    otherActionsAllowed: false,
  },
  {
    label: 'Stolen',
    isFilterOption: true,
    allowedUpdateTargets: [],
    willReissue: true,
    otherActionsAllowed: false,
  },
  {
    label: 'Requested in Error',
    isFilterOption: false,
    allowedUpdateTargets: [],
    willReissue: false,
    otherActionsAllowed: false,
  },
  {
    label: 'Closed',
    isFilterOption: true,
    allowedUpdateTargets: [],
    willReissue: false,
    otherActionsAllowed: false,
  },
  {
    label: 'Temporarily Closed',
    isFilterOption: true,
    allowedUpdateTargets: ['Active'],
    willReissue: false,
    otherActionsAllowed: false,
  },
];


/**
 * The card verification methods for which reset pin and change in actions are allowed.
 */
export const allowedCardActionsRequiredVerificationMethods=['PIN','Mandatory PIN'];


/**
 * Statuses included in the card filter dropdown options
 */
export const cardStatusFilterOptions = normaliseArray([
  'All',
  ...cardStatuses
    .filter((status) => status.isFilterOption)
    .map((status) => status.label)
    .sort(),
]);

/**
 * Statuses in the cards list table that have their checkboxes enabled
 */
export const isCardRowEnabled = cardStatuses
  .filter((status) => status.otherActionsAllowed || status.allowedUpdateTargets.length > 0)
  .map((status) => status.label);

/**
 * Statuses where other actions are allowed (Set PIN, Reset PIN, Transfer Card, Update Usage, Edit Card, Activate Card).
 */
export const editableCardStatuses = cardStatuses
  .filter((status) => status.otherActionsAllowed)
  .map((status) => status.label);

export function getCardStatus() {
  const cardDetail = useSelectCardDetails();
  return cardDetail?.cardStatus;
}
